import { SecurityClearance } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";

type FiltersByGroupId = Record<string, NonNullable<SecurityClearance["securityFilters"]>>;

/**
 * Fetches all SecurityClearance groups and returns their securityFilters
 * grouped by Group.id.
 *
 * Structure:
 *   {
 *     [groupId: string]: SecurityFilter[];
 *   }
 *
 * Does NOT apply user-specific logic—this simply loads all possible
 * security filters so that permission evaluation can be done locally.
 *
 * Cached via React Query and optimized to avoid unnecessary refetching.
 */
export function useSecurityFilters() {
  const { geotabClient, activeSessionId, clearGeotabClient } = useGeotabClient();

  return useQuery({
    queryKey: ["securityFiltersByGroup", activeSessionId],
    queryFn: ({ signal }) => {
      return geotabClient
        .get("Group", { id: "GroupSecurityId" }, 1, signal)
        .then(results => results as SecurityClearance[])
        .then((groups: SecurityClearance[]) => {
          return (groups ?? []).reduce<FiltersByGroupId>((acc, group) => {
            if (group.id && group.securityFilters?.length) {
              acc[group.id] = group.securityFilters;
            }
            return acc;
          }, {} as FiltersByGroupId);
        })
        .catch(e => {
          if (e instanceof Error && e.message.includes("Invalid session") && activeSessionId) {
            clearGeotabClient(activeSessionId);
            console.log("Session expired, clearing Geotab client");
          }
          return {} as FiltersByGroupId;
        });
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Determines whether the current user has a specific permission.
 *
 * Permission resolution rules:
 *   - A user's permission is determined by the securityFilters
 *     of the groups they belong to (me.securityGroups).
 *   - If ANY group explicitly removes (isAdd === false) the permission,
 *     the permission is denied, regardless of other groups.
 *
 * Uses cached filters from useSecurityFilters to avoid extra re-renders.
 */
export function useHasPermission(filterId: string) {
  const { me } = useGeotabClient();
  const { data: filtersByGroupId = {}, isLoading } = useSecurityFilters();

  const userGroupIds = me?.securityGroups?.map(g => g.id) ?? [];

  let denied = false;
  for (const groupId of userGroupIds) {
    const filters = filtersByGroupId[groupId];
    if (!filters) continue;

    for (const f of filters) {
      if (f.securityIdentifier !== filterId) continue;

      if (f.isAdd === false) {
        denied = true;
        break;
      }
    }

    if (denied) break;
  }
  return {
    hasPermission: !denied && !isLoading,
    isLoading,
  };
}
